<template>
  <div class="view-container">
    <HeroSection :imageUrl="$t(`summaryView.hero_${userConfiguration.type}.image.url`)">
      <template v-slot:normal>{{ $tc(`summaryView.hero_${userConfiguration.type}.heading`, 1) }}</template>
      <template v-slot:bold>{{ $tc(`summaryView.hero_${userConfiguration.type}.heading`, 2) }}</template>
    </HeroSection>
    <div style="flex: 1">
      <ContentContainer>
        <QuestionHeader v-html="$t('summaryView.section_USP.heading')" />
        <IconInfo>{{ $tc("summaryView.section_USP.text", 0) }}</IconInfo>
        <IconInfo>{{ $tc("summaryView.section_USP.text", 1) }}</IconInfo>
        <IconInfo>{{ $tc("summaryView.section_USP.text", 2) }}</IconInfo>
      </ContentContainer>
      <ContentContainer :is-green="true">
        <QuestionHeader>{{ $t("summaryView.section_PER_MONTH.heading") }}</QuestionHeader>
        <p class="cost-per-month">{{ summaryOutputContent.perMonth }}</p>
        <p class="small-paragraph">{{ $t("summaryView.section_PER_MONTH.text") }}</p>
      </ContentContainer>
      <ContentContainer>
        <QuestionHeader>{{ $t("summaryView.section_EXPLANATION.heading") }}</QuestionHeader>
        <p class="normal-paragraph">{{ $t("summaryView.section_EXPLANATION.text") }}</p>
        <router-link :to="{ name: 'CalculateView' }" style="text-decoration: none;">
          <span class="back-link">&larr; {{ $t("summaryView.section_EXPLANATION.link") }}</span>
        </router-link>
      </ContentContainer>
      <ContentContainer :is-green="true">
        <h2 class="flex-key-value data-header">
          <span>{{ $t("summaryView.section_CONSERVATORY.heading") }}</span>
          <span>
            {{ $t("general.other.approx") }}
            {{ summaryOutputContent.conservatory.total }}
          </span>
        </h2>
        <p
          class="small-paragraph left-align margin-vertical-small"
        >{{ $t("summaryView.section_CONSERVATORY.text") }}</p>
        <div class="flex-key-value wrap">
          <span class="flex-item">{{ $tc("summaryView.section_CONSERVATORY.items", 0) }}</span>
          <span class="flex-item">{{ summaryOutputContent.conservatory.type }}</span>
          <span class="flex-item">{{ $tc("summaryView.section_CONSERVATORY.items", 1) }}</span>
          <span class="flex-item">{{ summaryOutputContent.conservatory.size }}</span>
          <span class="flex-item">{{ $tc("summaryView.section_CONSERVATORY.items", 2) }}</span>
          <span class="flex-item">{{ summaryOutputContent.conservatory.season }}</span>
        </div>
      </ContentContainer>
      <ContentContainer>
        <h2 class="flex-key-value data-header">
          <span>{{ $t("summaryView.section_CONSTRUCTION.heading") }}</span>
          <span>
            {{ $t("general.other.approx") }}
            {{ summaryOutputContent.construction.total }}
          </span>
        </h2>
        <p
          class="small-paragraph left-align margin-vertical-small"
        >{{ summaryOutputContent.construction.description }}</p>
        <div class="flex-key-value wrap">
          <span class="flex-item">{{ $tc("summaryView.section_CONSTRUCTION.items", 1) }}</span>
          <span class="flex-item">{{ summaryOutputContent.construction.staff }}</span>
          <span
            v-if="shouldTaxDeductionsRender"
            class="flex-item"
          >{{ $tc("summaryView.section_CONSTRUCTION.items", 2) }}</span>
          <span
            v-if="shouldTaxDeductionsRender"
            class="flex-item"
          >{{ summaryOutputContent.construction.taxDeductions }}</span>
        </div>
      </ContentContainer>
      <ContentContainer :is-green="true">
        <h2 class="flex-key-value data-header">
          <span>{{ $t("summaryView.section_FOUNDATION.heading") }}</span>
          <span>
            {{ $t("general.other.approx") }}
            {{ summaryOutputContent.foundation.total }}
          </span>
        </h2>
        <p
          class="small-paragraph left-align margin-vertical-small"
        >{{ summaryOutputContent.foundation.description }}</p>
        <div class="flex-key-value wrap">
          <span class="flex-item">{{ $tc("summaryView.section_FOUNDATION.items", (this.$i18n.locale === 'sv' || this.$i18n.locale === 'fi') ? 0 : 1) }}</span>
          <span class="flex-item">{{ summaryOutputContent.foundation.material }}</span>
          <span class="flex-item">{{ $tc("summaryView.section_FOUNDATION.items", (this.$i18n.locale === 'sv' || this.$i18n.locale === 'fi') ? 1 : 2) }}</span>
          <span class="flex-item">{{ summaryOutputContent.foundation.staff }}</span>
          <span
            v-if="shouldTaxDeductionsRender"
            class="flex-item"
          >{{ $tc("summaryView.section_FOUNDATION.items", 2) }}</span>
          <span
            v-if="shouldTaxDeductionsRender"
            class="flex-item"
          >{{ summaryOutputContent.foundation.taxDeductions }}</span>
        </div>
      </ContentContainer>
      <ContentContainer>
        <h2 class="flex-key-value price-header">
          <span class="total-label">{{ $t("summaryView.section_TOTAL.heading") }}</span>
          <span class="total-cost">
            {{ $t("general.other.approx") }}
            {{ summaryOutputContent.total }}
          </span>
        </h2>
        <div
          v-if="!this.isUserProspect"
          class="email-input-container"
        >
          <label
            for="email"
            class="email-label normal-paragraph">
            {{ $t("summaryView.emailLabel") }}
          </label>
          <input
            type="email"
            id="email"
            :class="`email-input${emailInputError ? ' has-error' : ''}`"
            v-model="userEmail"
            @keyup.enter="handleSubmit"
            :placeholder="$t('summaryView.emailPlaceholder')"
          />
          <p
            v-if="emailInputError"
            class="email-error-text small-paragraph">
            {{ emailInputErrorText }}
          </p>
          <input
            type="checkbox"
            id="opt-in"
            class="opt-in-checkbox"
            v-model="userOptIn">
          <label
            for="opt-in"
            class="opt-in-label">
            {{ $t("summaryView.marketingOptIn") }}
          </label>
        </div>
        <CtaButton
          :disabled="
            mailStatus === 'pending' || mailStatus === 'success' ? true : false
          "
          @cta-click="handleSubmit"
        >{{ $t("summaryView.cta") }}</CtaButton>
        <p
          v-if="mailStatus === 'success' || mailStatus === 'error'"
          :class="
            `mail-status-text${mailStatus === 'error' ? ' has-error' : ''}`
          "
        >{{ mailStatusText }}</p>
        <a
          :href="$t('summaryView.ctaLink.url')"
          target="_blank"
          rel="noopener noreferrer"
          class="cta-link"
          v-html="$t('summaryView.ctaLink.link')"
        />
        <span class="cta-link-info">{{ $t("summaryView.ctaLink.info") }}</span>
      </ContentContainer>
      <ContentContainer :is-green="true">
        <p class="disclaimer">{{ $t("summaryView.disclaimer") }}</p>
      </ContentContainer>
    </div>
  </div>
</template>

<script>
import HeroSection from "../components/HeroSection";
import CtaButton from "../components/form/CtaButton";
import IconInfo from "../components/IconInfo";
import ContentContainer from "../components/ContentContainer";
import QuestionHeader from "../components/form/QuestionHeader";

export default {
  name: "SummaryView",
  props: ["isUserProspect", "userConfiguration", "priceInfo", "timeInfo"],
  components: {
    HeroSection,
    CtaButton,
    IconInfo,
    ContentContainer,
    QuestionHeader
  },
  data: function() {
    return {
      userEmail: null,
      userOptIn: true,
      emailInputError: null, // Can also be "empty" or "invalid"
      mailStatus: null // Can also be "pending", "success" or "error"
    };
  },
  computed: {
    shouldTaxDeductionsRender: function() {
      if (this.$i18n.locale === 'sv' || this.$i18n.locale === 'fi') {
        return this.userConfiguration.type === 'fixed';
      } else return false;
    },
    summaryOutputContent: function() {
      const currencySymbol = this.$i18n.locale === "fi" ? "€" : "kr";
      return {
        // No image in first version of app
        image: {
          url: null,
          alt: null
        },
        total: this.$options.filters.formatPrice(this.priceInfo.total, currencySymbol),
        perMonth: this.$options.filters.formatPrice(this.priceInfo.perMonth, currencySymbol),
        conservatory: {
          total: this.$options.filters.formatPrice(
            this.priceInfo.conservatory.total,
            currencySymbol
          ),
          type: this.$i18n.t(
            `summaryView.values.${this.userConfiguration.type}`
          ),
          size: this.$i18n.t(
            `summaryView.values.${this.userConfiguration.size}.${this.userConfiguration.type}`
          ),
          season: this.$i18n.t(
            `summaryView.values.${this.userConfiguration.season}`
          )
        },
        construction: {
          description: (() => {
            if (this.userConfiguration.constructionSupport === "none") {
              return this.$i18n.tc("summaryView.section_CONSTRUCTION.text", 0, {
                n: this.timeInfo.constructionTime
              });
            }

            if (
              this.userConfiguration.constructionSupport === "needs_support"
            ) {
              return `${this.$i18n.tc(
                "summaryView.section_CONSTRUCTION.text",
                1,
                { n: this.timeInfo.constructionTime }
              )} ${this.$i18n.tc("summaryView.section_CONSTRUCTION.text", 2, {
                n: this.$options.filters.formatPrice(
                  this.priceInfo.meta.staffPerHour,
                  currencySymbol
                )
              })}`;
            }

            return "";
          })(),
          total: this.$options.filters.formatPrice(
            this.priceInfo.construction.total,
            currencySymbol
          ),
          staff: this.$options.filters.formatPrice(
            this.priceInfo.construction.staff,
            currencySymbol
          ),
          taxDeductions: this.$options.filters.formatPrice(
            this.priceInfo.construction.taxDeductions,
            currencySymbol
          )
        },
        foundation: {
          description: (() => {
            if (this.userConfiguration.foundation === "already_completed") {
              return this.$i18n.tc("summaryView.section_FOUNDATION.text", 0);
            }

            if (this.userConfiguration.constructionSupport === "none") {
              return `${this.$i18n.tc(
                "summaryView.section_FOUNDATION.text",
                1,
                {
                  n: this.$i18n.t(
                    `summaryView.values.${this.userConfiguration.foundation}`
                  )
                }
              )} ${this.$i18n.tc("summaryView.section_FOUNDATION.text", 2, {
                n: this.$i18n.t(
                  `summaryView.values.${this.userConfiguration.size}.${this.userConfiguration.type}`
                )
              })} ${this.$i18n.tc(
                "summaryView.section_FOUNDATION.text_extra",
                0,
                {
                  n: this.timeInfo.foundationTime
                }
              )}`;
            }

            if (
              this.userConfiguration.constructionSupport === "needs_support"
            ) {
              return `${this.$i18n.tc(
                "summaryView.section_FOUNDATION.text",
                1,
                {
                  n: this.$i18n.t(
                    `summaryView.values.${this.userConfiguration.foundation}`
                  )
                }
              )} ${this.$i18n.tc("summaryView.section_FOUNDATION.text", 2, {
                n: this.$i18n.t(
                  `summaryView.values.${this.userConfiguration.size}.${this.userConfiguration.type}`
                )
              })} ${this.$i18n.tc(
                "summaryView.section_FOUNDATION.text_extra",
                1,
                {
                  n: this.timeInfo.foundationTime
                }
              )} ${this.$i18n.tc(
                "summaryView.section_FOUNDATION.text_extra",
                2,
                {
                  n: this.$options.filters.formatPrice(
                    this.priceInfo.meta.staffPerHour,
                    currencySymbol
                  )
                }
              )}`;
            }

            return "";
          })(),
          total: this.$options.filters.formatPrice(
            this.priceInfo.foundation.total,
            currencySymbol
          ),
          material: this.$options.filters.formatPrice(
            this.priceInfo.foundation.material,
            currencySymbol
          ),
          staff: this.$options.filters.formatPrice(
            this.priceInfo.foundation.staff,
            currencySymbol
          ),
          taxDeductions: this.$options.filters.formatPrice(
            this.priceInfo.foundation.taxDeductions,
            currencySymbol
          )
        }
      };
    },
    emailInputErrorText: function() {
      if (this.emailInputError === "empty") {
        return this.$i18n.t("summaryView.emailErrorEmpty");
      }

      if (this.emailInputError === "invalid") {
        return this.$i18n.t("summaryView.emailErrorInvalid");
      }

      return null;
    },
    mailStatusText: function() {
      if (this.mailStatus === "success") {
        return this.$i18n.t("summaryView.mailSuccess");
      }

      if (this.mailStatus === "error") {
        return this.$i18n.t("summaryView.mailError");
      }

      return null;
    }
  },
  watch: {
    userConfiguration: function() {
      // If the user already sent mail and, then, updates
      // the configuration --- reset the mail status (text)
      if (this.mailStatus) {
        this.mailStatus = null;
      }
    }
  },
  methods: {
    handleSubmit: function() {
      if (this.mailStatus === 'pending' || this.mailStatus === 'success') {
        return;
      }

      // If user has been identified as prospect
      // (no email input)
      if (this.isUserProspect) {
        this.postEstimateWithMail();
      // Else if email input is empty
      } else if (!this.userEmail) {
        this.emailInputError = "empty";
      // Else if email input is invalid
      } else if (!this.validEmail(this.userEmail)) {
        this.emailInputError = "invalid";
      // Else (post estimate with email input as summaryMailReceiver)
      } else {
        this.emailInputError = null;
        this.postEstimateWithMail();
      }
    },
    validEmail: function (email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      
      return re.test(email);
    },
    postEstimateWithMail: function() {
      this.mailStatus = "pendning";

      this.$emit(
        "postEstimate",
        {
          userConfiguration: this.userConfiguration,
          priceInfo: this.priceInfo,
          timeInfo: this.timeInfo,
          summaryMail: {
            sendSummaryMail: true,
            summaryMailReceiver: this.userEmail, // can be null
            marketingOptIn: this.isUserProspect ? null : this.userOptIn,
            summaryMailContent: this.summaryOutputContent
          }
        },
        {
          onSuccess: () => {
            this.mailStatus = "success";
          },
          onError: () => {
            this.mailStatus = "error";
          }
        }
      );
    }
  }
};
</script>

<style scoped lang="scss">
.cost-per-month {
  color: var(--sale-price);
  font-family: var(--font-brand);
  font-size: 36px;
  font-weight: 700;
  margin-top: 15px;
  margin-bottom: 15px;
}

.back-link {
  display: block;
  margin: 30px 0px 15px;
  font-family: var(--font-brand);
  font-size: 18px;
  font-weight: 700;
  color: (--primary-green);
  text-decoration: underline;

  &:hover {
    color: (--primary-darkgreen);
  }
}

.email-input-container {
  text-align: initial;
  margin: 45px 0 0;

  @media (min-width: $medium) {
    margin-top: 60px;
  }

  @media (min-width: $large) {
    margin-top: 75px;
  }

  .email-label {
    display: block;
    margin-bottom: 10px;
  }

  .email-input {
    outline: none;
    box-sizing: border-box;
    width: 100%;
    border: 1px solid var(--secondary-lightgray);
    border-radius: 0;
    padding: 20px 15px;
    font-family: var(--font-default);
    font-size: 16px;
    font-weight: 500;
    color: var(--primary-darkgreen);

    @media (min-width: $x-large) {
      font-size: 18px;
    }

    &.has-error {
      border-color: var(--sale-price);
    }
  }

  .email-error-text {
    margin-top: 15px;
    color: var(--sale-price);
    font-weight: 500;
  }

  .opt-in-label {
    display: block;
    margin-top: 15px;
    padding-left: 37px;
    font-family: var(--font-default);
    font-size: 12px;
    line-height: 1.4;
    cursor: pointer;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      margin: 2px 4px;
      width: 22px;
      height: 22px;
      border: 1px solid var(--secondary-lightgray);

      @media (min-width: $medium) {
        top: 2px;
      }
    }
  }

  .opt-in-checkbox {
    display: none;

    &:checked {
      + .opt-in-label {
        &::before {
          border-color: (--primary-green);
        }

        &::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          margin: 6px 7px;
          width: 16px;
          height: calc(16px / 2);
          border-bottom: 2px solid black;
          border-left: 2px solid black;
          -webkit-transform: rotate(-45deg);
          transform: rotate(-45deg);

          @media (min-width: $medium) {
            top: 2px;
          }
        }
      }
    }
  }
}

.mail-status-text {
  margin-top: -10px;
  margin-bottom: 45px;
  font-family: var(--font-default);

  &.has-error {
    color: var(--sale-price);
    font-weight: 500;
  }
}

.cta-link {
  @extend .back-link;

  line-height: 1.3;
}

.cta-link-info {
  @extend .small-paragraph;

  display: block;
  margin-top: -12px;
}

.disclaimer {
  @extend .small-paragraph;

  font-size: 13px;
}

/* Placeholder */
.bottom-img {
  background-image: url(https://s.skbv.se/static/images/uterum-kompletta-fasadmonterade-uterum-onska-%E2%80%93-fristaende-404324/LFC010/4);
  width: 100%;
  height: 25vh;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 50% 100%;
  position: relative;
}

.flex-key-value {
  display: flex;
  justify-content: space-between;
}

.data-header {
  color: var(--primary-green);
  font-size: 18px;
  font-weight: 700;
}

.price-header {
  align-items: baseline;
  color: var(--sale-price);
  font-weight: 700;
}

.total-cost {
  font-size: 22px;
}

.total-label {
  font-size: 18px;
}

@media (min-width: $medium) {
  .total-cost {
    font-size: 1.35em;
  }
  .total-label {
    font-size: 22px;
  }
  .data-header {
    font-size: 20px;
  }
}
.wrap {
  flex-wrap: wrap;
}

.flex-item {
  flex: 0 50%;
  margin: 5px 0px;
  font-family: var(--font-default);
  font-size: 14px;
}

.flex-item:nth-child(even) {
  text-align: right;
}

.flex-item:nth-child(odd) {
  text-align: left;
}

@media (min-width: $large) {
  .data-header {
    font-size: 22px;
  }
  .flex-item {
    font-size: 16px;
  }
  .view-container {
    display: flex;
  }
  .bottom-img {
    display: none;
  }
}
</style>
